import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import config from '../../config';

const getAttachmentName = (url, index) => {
    const fallback = `Attachment ${index + 1}`;
    try {
        const path = String(url || '').split('?')[0].split('#')[0];
        const name = path.split('/').filter(Boolean).pop();
        return name ? decodeURIComponent(name) : fallback;
    } catch (error) {
        return fallback;
    }
};

const isPdfUrl = (url) => /\.pdf(?:$|[?#])/i.test(String(url || ''));
const canRenderImagePreview = (url) => /^(https?:\/\/|data:image\/|blob:|\/)/i.test(String(url || ''));

const AttachmentCard = ({ url, index }) => {
    const [imageFailed, setImageFailed] = useState(false);
    const name = getAttachmentName(url, index);
    const isPdf = isPdfUrl(url);
    const showImage = !isPdf && !imageFailed && canRenderImagePreview(url);

    return (
        <a
            href={url}
            target='_blank'
            rel='noreferrer'
            className='group min-w-0 overflow-hidden rounded-[1rem] border border-scogoddd bg-white shadow-sm transition-all hover:border-scogopriamry2 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:rounded-[8px]'
            aria-label={`Open ${name}`}
        >
            <div className='relative aspect-[4/3] overflow-hidden bg-slate-100'>
                {showImage ? (
                    <img
                        src={url}
                        alt={name}
                        className='h-full w-full object-cover transition-transform duration-300 group-hover:scale-105'
                        onError={() => setImageFailed(true)}
                    />
                ) : (
                    <div className={`flex h-full w-full flex-col items-center justify-center ${isPdf ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-700'}`}>
                        <span className='material-icons text-font30 sm:text-[24px]'>{isPdf ? 'picture_as_pdf' : 'insert_drive_file'}</span>
                        <span className='mt-1 text-font11 font-bold uppercase sm:text-[9px]'>{isPdf ? 'PDF' : 'File'}</span>
                    </div>
                )}
                <span className='absolute right-2 top-2 flex h-[2.8rem] w-[2.8rem] items-center justify-center rounded-full bg-slate-900/70 text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus:opacity-100'>
                    <span className='material-icons text-font16'>open_in_new</span>
                </span>
            </div>
        </a>
    );
};

// Every asset detail the verification mail can put on the link, in the order
// they read best. Serial number and asset type are shown in the highlight block.
const DETAIL_FIELDS = [
    // { param: 'asset_number', label: 'Asset number', isMono: true },
    { param: 'make', label: 'Make' },
    { param: 'model', label: 'Model' },
    { param: 'partcode', label: 'Part code', isMono: true },
    // { param: 'asset_tag', label: 'Asset tag', isMono: true },
    { param: 'location_external_id', label: 'Location ID', isMono: true },
];

const DetailRow = ({ label, value, isMono }) => (
    <div className='min-w-0 rounded-[1rem] border border-scogoddd bg-white px-4 py-3 sm:rounded-[6px] sm:px-[8px] sm:py-[6px]'>
        <p className='text-font11 font-semibold uppercase tracking-wide text-scogolightgray sm:text-[8px]'>{label}</p>
        <p className={`mt-1 break-words font-bold text-scogo21 text-font13 sm:mt-[2px] sm:text-[11px] ${isMono ? 'font-mono' : ''}`}>{value}</p>
    </div>
);

const getApiMessage = (body, fallback) => (
    body?.message
    || body?.data?.message
    || body?.messages?.[0]
    || fallback
);

export default function PavPublicVerification() {
    const { asset_id: assetId, milestone_id: milestoneId } = useParams();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';
    const serialNumber = (searchParams.get('serial_number') || '').trim();
    const assetType = (searchParams.get('asset_type') || '').trim();
    const rawUrls = searchParams.get('urls') || '';
    const [submission, setSubmission] = useState({ status: 'idle', isVerified: null, message: '' });
    // Rejecting needs a reason, so the reject button opens a remark box and the
    // confirm inside it is what actually submits.
    const [isRejecting, setIsRejecting] = useState(false);
    const [rejectRemark, setRejectRemark] = useState('');
    const [remarkError, setRemarkError] = useState('');

    const attachmentUrls = useMemo(() => (
        rawUrls
            .split(',')
            .map((url) => url.trim())
            .filter(Boolean)
    ), [rawUrls]);

    // The mail link carries whatever the asset actually had, so only the params
    // that came through are rendered. Serial number and asset type get their own
    // spot above, so they are not repeated here.
    const details = useMemo(() => (
        DETAIL_FIELDS
            .map((field) => ({ ...field, value: (searchParams.get(field.param) || '').trim() }))
            .filter((field) => field.value)
    ), [searchParams]);

    const hasRequiredLinkData = Boolean(assetId && milestoneId && token);
    const isSubmitting = submission.status === 'submitting';
    const isComplete = submission.status === 'success';

    useEffect(() => {
        const previousTitle = document.title;
        document.title = 'PAV Verification | Scogo';
        return () => {
            document.title = previousTitle;
        };
    }, []);

    const submitVerification = async (isVerified, remark = '') => {
        if (!hasRequiredLinkData || isSubmitting || isComplete) return;

        setSubmission({ status: 'submitting', isVerified, message: '' });
        const apiBaseUrl = String(config.api.scogoApiUrl || '').replace(/\/$/, '');
        const endpoint = `${apiBaseUrl}/v1/webhooks/assets/${encodeURIComponent(assetId)}/milestones/${encodeURIComponent(milestoneId)}/physical-verification`;
        const query = new URLSearchParams({
            token,
            is_verified: String(isVerified),
        });
        if (remark) query.set('remark', remark);

        try {
            const response = await fetch(`${endpoint}?${query.toString()}`, {
                method: 'GET',
                headers: { accept: 'application/json' },
                cache: 'no-store',
            });
            const responseText = await response.text();
            let responseBody = {};
            if (responseText) {
                try {
                    responseBody = JSON.parse(responseText);
                } catch (error) {
                    responseBody = { message: responseText };
                }
            }

            if (!response.ok) {
                setSubmission({
                    status: 'error',
                    isVerified,
                    message: response.status === 409
                        ? getApiMessage(responseBody, 'This verification has already been submitted.')
                        : 'We could not save your response right now. Please try again in a moment.',
                });
                return;
            }

            setIsRejecting(false);
            setSubmission({
                status: 'success',
                isVerified,
                message: getApiMessage(
                    responseBody,
                    isVerified ? 'Asset verified successfully.' : 'Asset rejection submitted successfully.'
                ),
            });
        } catch {
            setSubmission({
                status: 'error',
                isVerified,
                message: 'We could not save your response right now. Please try again in a moment.',
            });
        }
    };

    const submitRejection = () => {
        const remark = rejectRemark.trim();
        if (!remark) {
            setRemarkError('Please add a remark explaining the rejection.');
            return;
        }
        setRemarkError('');
        submitVerification(false, remark);
    };

    return (
        <main className='flex h-screen h-[100dvh] w-full overflow-hidden bg-white sm:items-center sm:justify-center sm:bg-scogof2 sm:p-[16px]'>
            <section className='flex h-full w-full flex-col overflow-hidden bg-white sm:h-[640px] sm:max-h-[calc(100dvh-32px)] sm:max-w-[440px] sm:rounded-[12px] sm:border sm:border-scogoddd sm:shadow-lg'>
                <header className='shrink-0 border-b border-scogoeee bg-gradient-to-b from-white to-scogof8 px-6 py-6 text-center sm:px-[20px] sm:py-[14px]'>
                    <img
                        src={process.env.PUBLIC_URL + '/img/scogo-logo-black-1.png'}
                        alt='Scogo'
                        className='mx-auto h-auto w-[14rem] sm:w-[105px]'
                    />
                    <div className='mt-5 flex items-center justify-center gap-3 text-scogoprimary sm:mt-[10px] sm:gap-[8px]'>
                        <span className='flex h-[3.6rem] w-[3.6rem] items-center justify-center rounded-full bg-scogolightblue sm:h-[28px] sm:w-[28px]'>
                            <span className='material-icons-outlined text-font18 sm:text-[15px]'>inventory_2</span>
                        </span>
                        <h1 className='text-font18 font-bold sm:text-[14px]'>Asset verification</h1>
                    </div>
                    <p className='mt-2 text-font12 text-scogolightgray sm:mt-[4px] sm:text-[10px]'>Please confirm the asset details.</p>
                </header>

                <div className='flex min-h-0 flex-1 flex-col gap-6 overflow-hidden px-5 py-6 sm:gap-[12px] sm:px-[20px] sm:py-[16px]'>
                    {!hasRequiredLinkData && (
                        <div role='alert' className='flex shrink-0 items-start gap-3 rounded-[1rem] border border-red-200 bg-red-50 px-4 py-4 text-font13 text-red-800 sm:gap-[6px] sm:rounded-[8px] sm:px-[10px] sm:py-[8px] sm:text-[10px]'>
                            <span className='material-icons mt-0.5 text-font18 sm:text-[14px]'>error_outline</span>
                            <span>This verification link is incomplete or invalid.</span>
                        </div>
                    )}

                    {/* Details and attachments share one scroll area so the card keeps
                        its fixed height however many fields the link carries. */}
                    <div className='min-h-0 flex-1 space-y-6 overflow-y-auto overscroll-contain pr-1 sm:space-y-[12px] sm:pr-[4px]'>
                        <div className='rounded-[1rem] border border-scogoddd bg-scogolightblue px-5 py-4 sm:rounded-[8px] sm:px-[12px] sm:py-[10px]'>
                            <div className='flex items-start justify-between gap-3 sm:gap-[8px]'>
                                <div className='min-w-0'>
                                    <p className='text-font11 font-semibold uppercase tracking-wide text-scogopriamry2 sm:text-[9px]'>Serial number</p>
                                    <p className='mt-2 break-all font-mono text-font18 font-bold text-scogoprimary sm:mt-[4px] sm:text-[14px]'>{serialNumber || 'Not provided'}</p>
                                </div>
                                {assetType && (
                                    <span className='shrink-0 rounded-full border border-scogopriamry2 bg-white px-3 py-1 text-font11 font-bold text-scogoprimary sm:px-[8px] sm:py-[2px] sm:text-[9px]'>{assetType}</span>
                                )}
                            </div>
                        </div>

                        {details.length > 0 && (
                            <div>
                                <h2 className='mb-4 text-font14 font-bold text-slate-900 sm:mb-[8px] sm:text-[12px]'>Asset details</h2>
                                <div className='grid grid-cols-2 gap-3 sm:gap-[8px]'>
                                    {details.map((field) => (
                                        <DetailRow key={field.param} label={field.label} value={field.value} isMono={field.isMono} />
                                    ))}
                                </div>
                            </div>
                        )}

                        <div>
                            <div className='mb-4 flex items-center justify-between gap-3 sm:mb-[8px]'>
                                <h2 className='text-font14 font-bold text-slate-900 sm:text-[12px]'>Attachments</h2>
                                <span className='min-w-[2.6rem] rounded-full bg-slate-100 px-3 py-1 text-center text-font11 font-semibold text-slate-600 sm:min-w-[22px] sm:px-[6px] sm:py-[2px] sm:text-[9px]'>{attachmentUrls.length}</span>
                            </div>
                            {attachmentUrls.length > 0 ? (
                                <div className='grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-[10px]'>
                                    {attachmentUrls.map((url, index) => (
                                        <AttachmentCard key={`${url}-${index}`} url={url} index={index} />
                                    ))}
                                </div>
                            ) : (
                                <div className='flex flex-col items-center justify-center rounded-[1rem] border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-center sm:rounded-[8px] sm:py-[12px]'>
                                    <span className='material-icons-outlined text-font30 text-slate-400 sm:text-[24px]'>attachment</span>
                                    <p className='mt-2 text-font12 text-slate-600 sm:mt-[4px] sm:text-[10px]'>No attachments provided</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {submission.status === 'success' ? (
                        <div role='status' className={`shrink-0 rounded-[1rem] border px-5 py-5 text-center sm:rounded-[8px] sm:px-[12px] sm:py-[10px] ${submission.isVerified ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-red-200 bg-red-50 text-red-800'}`}>
                            <span className='material-icons text-font30 sm:text-[22px]'>{submission.isVerified ? 'check_circle' : 'cancel'}</span>
                            <p className='mt-2 text-font13 font-semibold sm:mt-[4px] sm:text-[10px]'>{submission.message}</p>
                        </div>
                    ) : (
                        <div className='shrink-0 border-t border-scogoeee pt-6 sm:pt-[12px]'>
                            <p className='text-center text-font14 font-semibold text-slate-900 sm:text-[11px]'>Does this asset match the details shown above?</p>
                            {submission.status === 'error' && (
                                <p role='alert' className='mt-4 rounded-[1rem] bg-red-50 px-4 py-3 text-center text-font13 leading-6 text-red-700 sm:mt-[8px] sm:rounded-[8px] sm:px-[10px] sm:py-[7px] sm:text-[10px] sm:leading-[14px]'>{submission.message}</p>
                            )}
                            {isRejecting ? (
                                <div className='mt-5 rounded-[1rem] border border-red-200 bg-red-50 px-4 py-4 sm:mt-[10px] sm:rounded-[8px] sm:px-[10px] sm:py-[8px]'>
                                    <label htmlFor='pav-reject-remark' className='text-font12 font-bold text-red-800 sm:text-[10px]'>
                                        Reason for rejection<span className='text-scogoclosed'> *</span>
                                    </label>
                                    <textarea
                                        id='pav-reject-remark'
                                        rows={3}
                                        autoFocus
                                        value={rejectRemark}
                                        disabled={isSubmitting}
                                        onChange={(event) => {
                                            setRejectRemark(event.target.value);
                                            if (remarkError) setRemarkError('');
                                        }}
                                        placeholder='Tell us what is wrong with this asset'
                                        className='mt-3 w-full resize-none rounded-[0.8rem] border border-scogoddd bg-white px-3 py-3 text-font13 text-slate-900 outline-none focus:border-scogoprimary sm:mt-[6px] sm:rounded-[6px] sm:px-[8px] sm:py-[6px] sm:text-[10px]'
                                    />
                                    {remarkError && <p role='alert' className='mt-2 text-font12 font-semibold text-scogoclosed sm:mt-[4px] sm:text-[9px]'>{remarkError}</p>}
                                    <div className='mt-4 grid grid-cols-1 gap-3 sm:mt-[8px] sm:grid-cols-2 sm:gap-[8px]'>
                                        <button
                                            type='button'
                                            disabled={isSubmitting}
                                            onClick={() => { setIsRejecting(false); setRejectRemark(''); setRemarkError(''); }}
                                            className={`flex min-h-[4.8rem] items-center justify-center rounded-[0.8rem] border border-scogoddd bg-white px-4 text-font14 font-bold text-slate-600 transition-colors sm:min-h-[36px] sm:rounded-[6px] sm:text-[11px] ${isSubmitting ? 'cursor-not-allowed opacity-50' : 'hover:bg-slate-100'}`}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type='button'
                                            disabled={!hasRequiredLinkData || isSubmitting}
                                            onClick={submitRejection}
                                            className={`flex min-h-[4.8rem] items-center justify-center gap-2 rounded-[0.8rem] bg-scogoclosed px-4 text-font14 font-bold text-white shadow-sm transition-colors sm:min-h-[36px] sm:rounded-[6px] sm:text-[11px] ${!hasRequiredLinkData || isSubmitting ? 'cursor-not-allowed opacity-50' : 'hover:bg-red-700'}`}
                                        >
                                            {isSubmitting && submission.isVerified === false
                                                ? <span className='material-icons animate-spin text-font18 sm:text-[14px]'>refresh</span>
                                                : <span className='material-icons text-font18 sm:text-[14px]'>close</span>}
                                            Confirm Reject
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className='mt-5 grid grid-cols-1 gap-3 sm:mt-[10px] sm:grid-cols-2 sm:gap-[8px]'>
                                    <button
                                        type='button'
                                        disabled={!hasRequiredLinkData || isSubmitting}
                                        onClick={() => { setRemarkError(''); setIsRejecting(true); }}
                                        className={`flex min-h-[4.8rem] items-center justify-center gap-2 rounded-[0.8rem] border border-scogoclosed bg-white px-4 text-font14 font-bold text-scogoclosed transition-colors sm:min-h-[36px] sm:rounded-[6px] sm:text-[11px] ${!hasRequiredLinkData || isSubmitting ? 'cursor-not-allowed opacity-50' : 'hover:bg-red-50 active:bg-red-100'}`}
                                    >
                                        <span className='material-icons text-font18 sm:text-[14px]'>close</span>
                                        Reject
                                    </button>
                                    <button
                                        type='button'
                                        disabled={!hasRequiredLinkData || isSubmitting}
                                        onClick={() => submitVerification(true)}
                                        className={`flex min-h-[4.8rem] items-center justify-center gap-2 rounded-[0.8rem] bg-scogoprimary px-4 text-font14 font-bold text-white shadow-sm transition-colors sm:min-h-[36px] sm:rounded-[6px] sm:text-[11px] ${!hasRequiredLinkData || isSubmitting ? 'cursor-not-allowed opacity-50' : 'hover:bg-scogopriamry2 active:bg-scogoprimary'}`}
                                    >
                                        {isSubmitting && submission.isVerified === true
                                            ? <span className='material-icons animate-spin text-font18 sm:text-[14px]'>refresh</span>
                                            : <span className='material-icons text-font18 sm:text-[14px]'>check</span>}
                                        Verify
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <footer className='shrink-0 border-t border-scogoeee bg-scogof8 px-4 py-3 text-center text-font11 text-scogogray sm:py-[7px] sm:text-[9px]'>
                    Secure physical asset verification
                </footer>
            </section>
        </main>
    );
}
